class PhcFacilitatorDirectory extends HTMLElement {
  connectedCallback() {
    if (this.initialized || this.initializationFrame) return;

    // Wait until the section parser has added every card and control. This also
    // makes the component reliable when Shopify reloads a section in the editor.
    this.initializationFrame = window.requestAnimationFrame(() => {
      this.initializationFrame = 0;
      this.initialize();
    });
  }

  disconnectedCallback() {
    if (this.initializationFrame) {
      window.cancelAnimationFrame(this.initializationFrame);
      this.initializationFrame = 0;
    }
    document.removeEventListener('keydown', this.handleKeydown);
    this.closeMobileFilters({ restoreScroll: true });
  }

  initialize() {
    if (this.initialized) return;

    this.grid = this.querySelector('[data-facilitator-grid]');
    this.cards = Array.from(this.querySelectorAll('[data-facilitator-card]'));
    this.filters = Array.from(this.querySelectorAll('[data-facilitator-filter]'));
    this.sorts = Array.from(this.querySelectorAll('[data-facilitator-sort]'));
    this.sort = this.sorts[0] || null;
    this.resets = Array.from(this.querySelectorAll('[data-facilitator-reset]'));
    this.reset = this.resets[0] || null;
    this.emptyState = this.querySelector('[data-facilitator-empty]');
    this.resultCount = this.querySelector('[data-facilitator-count]');
    this.activeFilterCounts = Array.from(this.querySelectorAll('[data-facilitator-active-count]'));
    this.activeFilterCount = this.activeFilterCounts[0] || null;
    this.mobilePanel = this.querySelector('[data-facilitator-filter-panel]');
    this.mobileOpen = this.querySelector('[data-facilitator-mobile-open]');
    this.mobileClose = this.querySelector('[data-facilitator-mobile-close]');
    this.mobileApply = this.querySelector('[data-facilitator-mobile-apply]');
    this.mobileBackdrop = this.querySelector('[data-facilitator-mobile-backdrop]');
    this.mobileOpenLabel = this.querySelector('[data-facilitator-mobile-open-label]');
    this.desktopFilters = this.querySelector('[data-facilitator-desktop-filters]');
    this.filterLoading = this.querySelector('[data-facilitator-filter-loading]');

    if (!this.grid) return;
    this.initialized = true;

    this.prepareExistingImages();

    const waitsForMarianaFilters = this.dataset.marianaEnrich !== 'false';
    this.setFilterState(waitsForMarianaFilters ? 'loading' : 'ready');

    this.filters.forEach((filter) => {
      if (!waitsForMarianaFilters) this.populateFilter(filter);
      const update = () => {
        this.syncFilterValue(filter);
        this.applyFilters();
      };
      filter.addEventListener('change', update);
      filter.addEventListener('input', update);
    });

    this.sorts.forEach((sort) => {
      sort.value = sort.dataset.defaultSort || 'daily';
      sort.addEventListener('change', () => {
        this.syncSortValue(sort);
        this.applySort();
        this.applyFilters();
      });
    });

    this.resets.forEach((reset) => {
      reset.addEventListener('click', () => {
        this.filters.forEach((filter) => { filter.value = ''; });
        this.sorts.forEach((sort) => { sort.value = sort.dataset.defaultSort || 'daily'; });
        this.applySort();
        this.applyFilters();
      });
    });

    this.mobileOpen?.addEventListener('click', (event) => {
      event.preventDefault();
      if (this.dataset.filterState !== 'ready') return;
      this.openMobileFilters();
    });
    this.mobileClose?.addEventListener('click', () => this.closeMobileFilters());
    this.mobileApply?.addEventListener('click', () => {
      this.applySort();
      this.applyFilters();
      this.closeMobileFilters();
    });
    this.mobileBackdrop?.addEventListener('click', () => this.closeMobileFilters());

    this.handleKeydown = (event) => {
      if (event.key === 'Escape' && this.classList.contains('is-mobile-filter-open')) {
        this.closeMobileFilters();
      }
    };
    document.addEventListener('keydown', this.handleKeydown);

    this.applySort();
    this.applyFilters();
    this.hydrateMarianaCards();
  }

  prepareExistingImages() {
    const marianaAuthoritative = this.dataset.marianaEnrich !== 'false';

    this.cards.forEach((card) => {
      const media = card.querySelector('[data-phc-facilitator-card-media]');
      if (!media) return;

      // When Mariana enrichment is enabled, Shopify media is only fallback data
      // in the HTML. It must never become visible/ready before Mariana matching
      // finishes, otherwise an old Shopify portrait can win the race.
      if (marianaAuthoritative) {
        // Mariana is authoritative for facilitator metadata as well as portraits.
        // Clear every Shopify/block fallback before filters or copy are rendered.
        ['location', 'sessions', 'languages', 'style'].forEach((key) => { card.dataset[key] = ''; });
        card.querySelectorAll('[data-phc-facilitator-card-field]').forEach((node) => {
          node.textContent = '';
          node.hidden = true;
        });

        media.querySelectorAll('img, video, picture').forEach((node) => {
          node.hidden = true;
          if (node.tagName === 'IMG') {
            node.removeAttribute('srcset');
            node.removeAttribute('sizes');
          }
        });
        card.dataset.hasUsableImage = 'false';
        this.setImageResolution(card, 'pending');
        return;
      }

      const image = media.querySelector('img');
      if (!image) {
        this.setImageResolution(card, 'missing');
        return;
      }

      const markLoaded = () => {
        if (!image.naturalWidth) return;
        image.hidden = false;
        card.dataset.hasUsableImage = 'true';
        this.setImageResolution(card, 'ready');
      };

      const markBroken = () => {
        image.hidden = true;
        card.dataset.hasUsableImage = 'false';
        this.setImageResolution(card, 'missing');
      };

      image.addEventListener('load', markLoaded);
      image.addEventListener('error', markBroken);
      if (image.complete) {
        if (image.naturalWidth > 0) markLoaded();
        else if (image.getAttribute('src')) markBroken();
      }
    });
  }

  normalizeIdentity(value) {
    return String(value || '')
      .replace(/[æÆ]/g, 'ae')
      .replace(/[øØ]/g, 'o')
      .replace(/[åÅ]/g, 'a')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase()
      .replace(/[^a-z0-9]+/g, '')
      .trim();
  }

  identityTokens(value) {
    return String(value || '')
      .replace(/[æÆ]/g, 'ae').replace(/[øØ]/g, 'o').replace(/[åÅ]/g, 'a')
      .normalize('NFKD').replace(/[\u0300-\u036f]/g, '')
      .toLocaleLowerCase().match(/[a-z0-9]+/g) || [];
  }

  namesAreCompatible(left, right) {
    const leftKey = this.normalizeIdentity(left);
    const rightKey = this.normalizeIdentity(right);
    if (!leftKey || !rightKey) return false;
    if (leftKey === rightKey) return true;

    const leftTokens = this.identityTokens(left);
    const rightTokens = this.identityTokens(right);
    if (!leftTokens.length || !rightTokens.length || leftTokens[0] !== rightTokens[0]) return false;

    // Supports intentional short Shopify names such as "Rasmus" / "Felina"
    // while still preventing a stale numeric Mariana ID from selecting another
    // person who happens to occupy that resource ID.
    const leftInRight = leftTokens.every((token) => rightTokens.includes(token));
    const rightInLeft = rightTokens.every((token) => leftTokens.includes(token));
    return leftInRight || rightInLeft;
  }

  isCardDisplayable(card) {
    if (!card) return false;
    // On the full Mariana directory, Mariana decides who exists. Once loading
    // has completed, stale Shopify-only cards are hidden rather than becoming
    // fallback facilitator records. Image availability never controls existence.
    if (this.isMarianaDirectorySource() && this.dataset.marianaDirectoryStatus === 'loaded') {
      return card.dataset.marianaProfileMatched === 'true';
    }
    return true;
  }

  setImageResolution(card, state) {
    if (!card) return;
    const nextState = ['pending', 'ready', 'missing'].includes(state) ? state : 'pending';
    card.dataset.imageResolution = nextState;
    if (nextState === 'pending') card.setAttribute('aria-busy', 'true');
    else card.removeAttribute('aria-busy');
  }

  profileForCard(card, profiles) {
    const pageTitle = String(card.dataset.pageTitle || card.dataset.name || card.querySelector('[data-phc-facilitator-card-name]')?.textContent || '').trim();
    const pageHandle = String(card.dataset.pageHandle || '').trim();
    if (!pageTitle && !pageHandle) return null;

    const sharedMatcher = window.PHCMarianaFacilitator?.matchProfile;
    if (typeof sharedMatcher === 'function') {
      const matched = sharedMatcher(profiles, { pageTitle, pageHandle });
      if (matched) return matched;
    }

    const aliasesFor = (profile) => [profile?.name, ...(Array.isArray(profile?.matchNames) ? profile.matchNames : [])]
      .map((value) => String(value || '').trim())
      .filter(Boolean);

    const exact = profiles.filter((profile) => aliasesFor(profile).some((alias) => {
      const key = this.normalizeIdentity(alias);
      return key && (key === this.normalizeIdentity(pageTitle) || key === this.normalizeIdentity(pageHandle));
    }));
    if (exact.length === 1) return exact[0];

    const compatible = profiles.filter((profile) => aliasesFor(profile).some((alias) =>
      this.namesAreCompatible(pageTitle, alias) || (pageHandle && this.namesAreCompatible(pageHandle, alias))
    ));
    return compatible.length === 1 ? compatible[0] : null;
  }

  isMarianaDirectorySource() {
    return this.dataset.marianaDirectorySource === 'true' && this.dataset.marianaEnrich !== 'false';
  }

  syncCardsFromMariana(profiles) {
    if (!this.isMarianaDirectorySource() || !this.grid) return;

    // Shopify facilitator pages are the whitelist. Mariana is enrichment only:
    // never create a storefront card for an employee/public profile that does
    // not have a matching Shopify page using the facilitator template.
    // This keeps service/sync/admin accounts and other non-facilitator Mariana
    // employees out of the public directory by construction.
    this.cards.forEach((card) => {
      const matched = this.profileForCard(card, profiles);
      if (!matched || matched.employeeActive === false) {
        card._phcMarianaProfile = null;
        card.dataset.marianaProfileMatched = 'false';
        return;
      }
      card._phcMarianaProfile = matched;
      card.dataset.marianaProfileMatched = 'true';
    });

    this.cards = Array.from(this.querySelectorAll('[data-facilitator-card]'));
  }

  loadImageElement(url, sourceImage, alt, timeoutMs = 12000) {
    const source = String(url || '').trim();
    if (!source || !sourceImage) return Promise.resolve(null);

    return new Promise((resolve) => {
      const candidate = new Image();
      let settled = false;

      // Preserve styling, dimensions, classes and data attributes, but never
      // inherit Shopify src/srcset values. The exact element that successfully
      // loads Mariana is moved into the DOM, so the image is requested once.
      Array.from(sourceImage.attributes).forEach((attribute) => {
        if (['src', 'srcset', 'sizes', 'loading', 'hidden', 'alt'].includes(attribute.name)) return;
        candidate.setAttribute(attribute.name, attribute.value);
      });
      candidate.alt = alt || sourceImage.alt || '';
      candidate.decoding = 'async';

      const finish = (result) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        candidate.onload = null;
        candidate.onerror = null;
        resolve(result ? candidate : null);
      };

      const timer = window.setTimeout(() => finish(false), timeoutMs);
      candidate.onload = () => finish(candidate.naturalWidth > 0);
      candidate.onerror = () => finish(false);
      candidate.src = source;
      if (candidate.complete && candidate.naturalWidth > 0) finish(true);
    });
  }

  setCardField(card, field, value) {
    if (!card) return;
    const text = String(value || '').trim();
    card.querySelectorAll(`[data-phc-facilitator-card-field="${field}"]`).forEach((node) => {
      node.textContent = text;
      node.hidden = !text;
    });
  }

  async updateCardFromMariana(card, profile) {
    if (!card || !profile) return false;

    card.dataset.marianaProfileMatched = 'true';

    // Never merge with Shopify/block fallback metadata. Mariana wins, including blank values.
    card.dataset.location = String(profile.location || '').trim();
    card.dataset.sessions = String(profile.sessionTypes || '').trim();
    card.dataset.languages = String(profile.languages || '').trim();
    card.dataset.style = String(profile.style || '').trim();
    this.setCardField(card, 'location', profile.location);
    this.setCardField(card, 'session_types', profile.sessionTypes);
    this.setCardField(card, 'positioning', profile.positioning);

    const media = card.querySelector('[data-phc-facilitator-card-media]');
    if (!media) {
      this.setImageResolution(card, 'missing');
      return false;
    }

    // A matched Mariana employee is authoritative for the card portrait.
    // Immediately retire every Shopify-provided image/video so stale media can
    // never remain visible while the exact employee detail is being resolved.
    media.querySelectorAll('img, video, picture').forEach((node) => {
      node.hidden = true;
      if (node.tagName === 'IMG') {
        node.removeAttribute('srcset');
        node.removeAttribute('sizes');
      }
    });
    card.dataset.hasUsableImage = 'false';
    this.setImageResolution(card, 'pending');

    let target = media.querySelector('img[data-phc-mariana-image="true"]');
    if (!target) {
      target = document.createElement('img');
      target.dataset.phcMarianaImage = 'true';
      target.setAttribute('data-phc-facilitator-card-image', '');
      target.width = 1200;
      target.height = 1629;
      target.decoding = 'async';
      target.loading = 'lazy';
      target.hidden = true;
      media.appendChild(target);
    }

    const imageSources = [
      profile.thumbnailImageUrl,
      profile.imageUrl,
      profile.largeImageUrl
    ].map((value) => String(value || '').trim()).filter((value, index, values) => value && values.indexOf(value) === index);
    if (!imageSources.length) {
      target.hidden = true;
      this.setImageResolution(card, 'missing');
      return false;
    }

    const marianaAlt = `${profile.name || card.dataset.name || 'Plugin Heat Club facilitator'}, Plugin Heat Club facilitator`;
    const sharedLoader = window.PHCMarianaFacilitator?.loadImageCandidates;
    let loadedImage = null;

    if (typeof sharedLoader === 'function') {
      const result = await sharedLoader(imageSources, {
        template: target,
        alt: marianaAlt,
        timeoutMs: 15000,
        retries: 1,
        revision: '55'
      });
      loadedImage = result?.image || null;
    } else {
      // Defensive fallback for unusual script ordering. Try every normalized
      // Mariana URL instead of failing permanently on a bad thumbnail.
      for (const sourceUrl of imageSources) {
        let imageUrl = sourceUrl;
        try {
          const url = new URL(sourceUrl, window.location.href);
          url.searchParams.set('shopify_theme', '55');
          imageUrl = url.toString();
        } catch (_) {}
        loadedImage = await this.loadImageElement(imageUrl, target, marianaAlt, 15000);
        if (loadedImage) break;
      }
    }

    if (!loadedImage) {
      target.hidden = true;
      this.setImageResolution(card, 'missing');
      return false;
    }

    loadedImage.dataset.phcMarianaImage = 'true';
    loadedImage.setAttribute('data-phc-facilitator-card-image', '');
    loadedImage.hidden = false;

    // Remove old Shopify media only after the Mariana image has been verified.
    media.querySelectorAll('img, video, picture').forEach((node) => {
      if (node !== target && node !== loadedImage) node.remove();
    });
    if (target.isConnected) target.replaceWith(loadedImage);
    else media.appendChild(loadedImage);

    card.dataset.hasUsableImage = 'true';
    this.setImageResolution(card, 'ready');
    card.hidden = false;
    card.setAttribute('aria-hidden', 'false');
    return true;
  }

  async hydrateMarianaCards() {
    if (this.dataset.marianaEnrich === 'false' || (!this.cards.length && !this.isMarianaDirectorySource())) return;

    const api = window.PHCMarianaFacilitator;
    const proxyEndpoint = String(this.dataset.marianaProxyUrl || '').trim();
    const baseUrl = String(this.dataset.marianaApiBaseUrl || '').trim();
    if (!api?.fetchDirectoryProfiles || !proxyEndpoint) {
      this.cards.forEach((card) => this.setImageResolution(card, 'missing'));
      this.dataset.marianaDirectoryStatus = 'proxy-unavailable';
      this.setFilterState('unavailable');
      this.applyFilters();
      return;
    }

    this.dataset.marianaDirectoryStatus = 'loading';
    this.cards.forEach((card) => this.setImageResolution(card, 'pending'));

    let profiles = [];
    try {
      profiles = await api.fetchDirectoryProfiles({
        baseUrl,
        proxyEndpoint,
        maxProfiles: Math.max(Number(this.dataset.marianaMaxProfiles || 250), 250)
      });
    } catch (error) {
      console.error('[PHC] Mariana facilitator feed failed:', error);
      this.cards.forEach((card) => this.setImageResolution(card, 'missing'));
      this.dataset.marianaDirectoryStatus = 'feed-error';
      this.setFilterState('unavailable');
      this.applyFilters();
      return;
    }

    this.syncCardsFromMariana(profiles);

    const jobs = this.cards.map((card) => async () => {
      const pageTitle = String(card.dataset.pageTitle || card.dataset.name || '').trim();
      const pageHandle = String(card.dataset.pageHandle || '').trim();
      let matched = card._phcMarianaProfile || this.profileForCard(card, profiles);

      if (!matched) {
        this.setImageResolution(card, 'missing');
        card.dataset.marianaProfileMatched = 'false';
        return;
      }

      card.dataset.marianaProfileMatched = 'true';
      if (matched.publicProfileId || matched.id) {
        card.dataset.marianaResolvedProfileId = String(matched.publicProfileId || matched.id);
      }
      if (matched.employeeId) {
        card.dataset.marianaResolvedEmployeeId = String(matched.employeeId);
      }

      // Mariana employee profiles carry location access. If the bulk feed did
      // not expose it, resolve the already matched employee detail as a second
      // source. This is still Mariana data: never fall back to Shopify labels.
      const needsEmployeeDetail = !String(matched.imageUrl || '').trim() || (!String(matched.location || '').trim() && matched.employeeLocationLookupComplete !== true);
      if (needsEmployeeDetail && matched.employeeId && api.fetchEmployeeDetail) {
        try {
          const detail = await api.fetchEmployeeDetail(proxyEndpoint, matched.employeeId);
          if (detail && this.namesAreCompatible(pageTitle || pageHandle, detail.name || '')) {
            matched = {
              ...matched,
              ...detail,
              location: String(detail.location || matched.location || '').trim(),
              sessionTypes: String(detail.sessionTypes || matched.sessionTypes || '').trim(),
              languages: String(detail.languages || matched.languages || '').trim(),
              style: String(detail.style || matched.style || '').trim(),
              imageUrl: String(detail.imageUrl || matched.imageUrl || '').trim(),
              largeImageUrl: String(detail.largeImageUrl || matched.largeImageUrl || '').trim(),
              thumbnailImageUrl: String(detail.thumbnailImageUrl || matched.thumbnailImageUrl || '').trim()
            };
          }
        } catch (error) {
          console.info('[PHC] Mariana employee detail fallback failed:', pageHandle || pageTitle, error?.message || error);
        }
      }

      await this.updateCardFromMariana(card, matched);
    });

    // Limit concurrent portrait downloads. This avoids a burst of 20-60
    // simultaneous proxy/image requests on the homepage and directory.
    const workerCount = Math.min(4, jobs.length);
    let cursor = 0;
    const workers = Array.from({ length: workerCount }, async () => {
      while (cursor < jobs.length) {
        const index = cursor;
        cursor += 1;
        try {
          await jobs[index]();
        } catch (error) {
          console.info('[PHC] Mariana facilitator card failed:', error?.message || error);
        }
      }
    });
    await Promise.all(workers);

    // Do not expose filter controls until every matched facilitator has finished
    // its Mariana enrichment. This prevents empty/partial mobile filter options.
    this.dataset.marianaDirectoryStatus = 'loaded';
    this.filters.forEach((filter) => this.populateFilter(filter));
    this.applySort();
    this.applyFilters();
    this.setFilterState('ready');
  }

  setFilterState(state) {
    const next = ['loading', 'ready', 'unavailable'].includes(state) ? state : 'loading';
    this.dataset.filterState = next;

    const ready = next === 'ready';
    const loading = next === 'loading';
    if (this.mobileOpen) {
      this.mobileOpen.disabled = !ready;
      if (loading) this.mobileOpen.setAttribute('aria-busy', 'true');
      else this.mobileOpen.removeAttribute('aria-busy');
    }

    if (this.mobileOpenLabel) {
      const readyLabel = String(this.mobileOpenLabel.dataset.readyLabel || 'Filter');
      this.mobileOpenLabel.textContent = ready ? readyLabel : (loading ? 'Loading filters…' : 'Filters unavailable');
    }

    if (this.desktopFilters) this.desktopFilters.hidden = !ready;
    if (this.filterLoading) {
      this.filterLoading.hidden = !loading;
      const text = this.filterLoading.querySelector('span:last-child');
      if (text) text.textContent = loading ? 'Loading filters…' : '';
    }

    if (!ready && this.classList.contains('is-mobile-filter-open')) this.closeMobileFilters();
  }

  normalize(value) {
    return String(value || '')
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLocaleLowerCase();
  }

  syncFilterValue(source) {
    const key = source?.dataset?.filterKey;
    if (!key) return;
    this.filters.forEach((filter) => {
      if (filter !== source && filter.dataset.filterKey === key) filter.value = source.value;
    });
  }

  syncSortValue(source) {
    if (!source) return;
    this.sorts.forEach((sort) => {
      if (sort !== source) sort.value = source.value;
    });
    this.sort = source;
  }

  splitValues(value) {
    return String(value || '')
      .split(/[|,;\n/]+/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  getCardValues(card, key) {
    return this.splitValues(card.dataset[key]).map((item) => this.normalize(item));
  }

  populateFilter(filter) {
    const key = filter.dataset.filterKey;
    const values = new Map();

    filter.querySelectorAll('[data-generated-option]').forEach((option) => option.remove());

    this.cards.forEach((card) => {
      if (!this.isCardDisplayable(card)) return;
      this.splitValues(card.dataset[key]).forEach((label) => {
        const value = this.normalize(label);
        if (value && !values.has(value)) values.set(value, label);
      });
    });

    Array.from(values.entries())
      .sort((left, right) => left[1].localeCompare(right[1]))
      .forEach(([value, label]) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = label;
        option.dataset.generatedOption = 'true';
        filter.appendChild(option);
      });

    const label = filter.closest('label');
    if (label) label.hidden = values.size === 0;
  }

  dailyScore(card) {
    const now = new Date();
    const dateKey = `${now.getFullYear()}-${now.getMonth() + 1}-${now.getDate()}`;
    const value = `${dateKey}:${card.dataset.name || ''}`;
    let hash = 2166136261;
    for (let index = 0; index < value.length; index += 1) {
      hash ^= value.charCodeAt(index);
      hash = Math.imul(hash, 16777619);
    }
    return hash >>> 0;
  }

  applySort() {
    if (!this.grid || !this.cards.length) return;
    const mode = this.sorts?.find((sort) => sort.value)?.value || this.sort?.value || 'daily';
    const ordered = [...this.cards].sort((left, right) => {
      if (mode === 'az') {
        return String(left.dataset.name || '').localeCompare(String(right.dataset.name || ''));
      }
      return this.dailyScore(left) - this.dailyScore(right);
    });
    ordered.forEach((card) => this.grid.appendChild(card));
  }

  applyFilters() {
    let visible = 0;
    const selectedByKey = new Map();

    this.filters.forEach((filter) => {
      if (filter.closest('label')?.hidden) return;
      const key = filter.dataset.filterKey;
      const selected = this.normalize(filter.value);
      if (key && selected) selectedByKey.set(key, selected);
    });

    this.cards.forEach((card) => {
      const canDisplay = this.isCardDisplayable(card);
      const matchesFilters = Array.from(selectedByKey.entries()).every(([key, selected]) =>
        this.getCardValues(card, key).includes(selected)
      );
      const matches = canDisplay && matchesFilters;

      card.hidden = !matches;
      card.setAttribute('aria-hidden', matches ? 'false' : 'true');
      if (matches) visible += 1;
    });

    const active = selectedByKey.size;
    this.activeFilterCounts.forEach((counter) => {
      counter.textContent = String(active);
      counter.hidden = active === 0;
    });

    if (this.emptyState) {
      const shouldShowEmpty = this.dataset.enableFilters === 'true' && visible === 0;
      this.emptyState.hidden = !shouldShowEmpty;
    }
    if (this.resultCount) this.resultCount.textContent = String(visible);
  }

  openMobileFilters() {
    if (!this.mobilePanel || this.dataset.filterState !== 'ready') return;

    // The trigger itself only exists in the mobile UI. Do not gate the action
    // behind matchMedia: mobile Safari can briefly report a stale viewport
    // during orientation/address-bar changes, leaving a visible button inert.
    this.classList.add('is-mobile-filter-open');
    this.dataset.mobileFilterOpen = 'true';
    document.documentElement.classList.add('phc-mobile-filter-open');
    document.body.classList.add('phc-mobile-filter-open');

    if (this.mobileBackdrop) {
      this.mobileBackdrop.hidden = false;
      this.mobileBackdrop.style.display = 'block';
      this.mobileBackdrop.style.zIndex = '2147483000';
    }

    // Inline open-state properties deliberately win over the theme's several
    // facilitator CSS layers. They are removed again on close, so desktop keeps
    // its normal sticky filter bar after a resize/orientation change.
    this.mobilePanel.hidden = false;
    this.mobilePanel.style.display = 'flex';
    this.mobilePanel.style.visibility = 'visible';
    this.mobilePanel.style.opacity = '1';
    this.mobilePanel.style.pointerEvents = 'auto';
    this.mobilePanel.style.zIndex = '2147483001';
    this.mobileOpen?.setAttribute('aria-expanded', 'true');
    this.mobilePanel.setAttribute('aria-modal', 'true');
    this.mobilePanel.setAttribute('role', 'dialog');

    window.requestAnimationFrame(() => {
      try {
        this.mobileClose?.focus({ preventScroll: true });
      } catch (error) {
        this.mobileClose?.focus();
      }
    });
  }

  closeMobileFilters(options = {}) {
    if (!this.mobilePanel) return;

    const wasOpen = this.classList.contains('is-mobile-filter-open');
    this.classList.remove('is-mobile-filter-open');
    delete this.dataset.mobileFilterOpen;
    document.documentElement.classList.remove('phc-mobile-filter-open');
    document.body.classList.remove('phc-mobile-filter-open');
    if (this.mobileBackdrop) {
      this.mobileBackdrop.hidden = true;
      this.mobileBackdrop.style.removeProperty('display');
      this.mobileBackdrop.style.removeProperty('z-index');
    }
    this.mobilePanel.hidden = true;
    this.mobilePanel.style.removeProperty('display');
    this.mobilePanel.style.removeProperty('visibility');
    this.mobilePanel.style.removeProperty('opacity');
    this.mobilePanel.style.removeProperty('pointer-events');
    this.mobilePanel.style.removeProperty('z-index');
    this.mobileOpen?.setAttribute('aria-expanded', 'false');
    this.mobilePanel.removeAttribute('aria-modal');
    this.mobilePanel.removeAttribute('role');

    if (wasOpen && !options.restoreScroll) {
      try {
        this.mobileOpen?.focus({ preventScroll: true });
      } catch (error) {
        this.mobileOpen?.focus();
      }
    }
  }
}

if (!customElements.get('phc-facilitator-directory')) {
  customElements.define('phc-facilitator-directory', PhcFacilitatorDirectory);
}
